import React, {memo, MouseEvent, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../../../components/PasswordContainer'
import {SettingsPageContainer} from '../../../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../../../components/Settings/SettingsList';
import {MenuContent, MenuEntity} from '../../../../../../store/control/state';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {UpDown} from "../../components/UpDown";
import {useRPC} from '../../../../../../hooks/rpc';
import {useLocalization} from "../../../../../../hooks/localization";
import {changeBusyAction} from '../../../../../../store/ui/actions';
import {useDispatch} from 'react-redux';

const keyExtractor = (item: MenuContent) => item.type + (item.relational_id ? item.relational_id : '');

export const ContentListScreen = memo(function ContentListScreen() {
    const {t} = useLocalization();
    const [rpc] = useRPC();
    const [selectedContent, setSelectedContent] = useState<MenuContent>(null);
    const params = useParams<{ id: string }>();
    const dispatch = useDispatch();
    const menuList = useSelector(state => state.control.menuList);
    const selectedMenuItem = useMemo(() => menuList.find((item) => item.id === params.id), [menuList, params.id]);
    const contentListSorted = useMemo(() => selectedMenuItem?.content_list?.sort((a, b) => a.order - b.order) || [], [selectedMenuItem?.content_list]);

    const controlTypes = useSelector(state => state.control.controlTypeList);
    const controlTypeOptionList =
        useMemo(() => controlTypes
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(controlType => ({
                value: controlType.id,
                label: controlType.name,
            })), [controlTypes]);


    const buttons = useMemo(() => [
        {
            text: t('edit'),
            url: `/settings/left-menu/${params.id}/contents/edit/${selectedContent ? selectedContent.type : ''}/${selectedContent ? selectedContent?.relational_id : ''}`,
            className: 'orange',
            hidden: !(selectedContent),
        },
        {
            text: t('add'),
            url: `/settings/left-menu/${params.id}/contents/edit/`,
            className: 'green',
        },
    ], [t, params.id, selectedContent]);

    const swapItemOrders = useCallback((itemA: MenuContent, itemB: MenuContent) => {
        const tempAOrder = itemA.order;
        const tempBOrder = itemB.order;
        const newContents: MenuContent[] = contentListSorted.map((item) => {
            if (itemA.type === item.type && (item.relational_id ? itemA.relational_id === item.relational_id : true)) {
                item.order = tempBOrder;
            }
            if (itemB.type === item.type && (item.relational_id ? itemB.relational_id === item.relational_id : true)) {
                item.order = tempAOrder;
            }
            return item;
        });

        const menuEntity: MenuEntity = {
            ...selectedMenuItem,
            content_list: newContents,
        };
        dispatch(changeBusyAction(true))
        rpc.updateMenu(menuEntity).then(() => {
          setSelectedContent(null)
          dispatch(changeBusyAction(false))
        });
    }, [contentListSorted, selectedMenuItem, rpc, dispatch]);


    const onUpClick = useCallback((index: number) => {
        swapItemOrders(contentListSorted[index - 1], contentListSorted[index]);
    }, [swapItemOrders, contentListSorted]);

    const onDownClick = useCallback((index: number) => {
        swapItemOrders(contentListSorted[index + 1], contentListSorted[index]);
    }, [swapItemOrders, contentListSorted]);

    const mediaTypeOptionsList = useMemo(() => [{label: t('air_conditioning'), value: 'air'}, {label: t('other'), value: 'other'}], [t]) ;

    const displayNameExtractor = useCallback((item: MenuContent, column: ColumnModel, index?: number) => {
        if (column.key === 'type') {
            switch (item.type) {
                case 'control':
                    return t('control');
                case 'media':
                    return t('media');
                default:
                    return '-';
            }
        } else if (column.key === 'relational_id') {
            return item.type === 'control' ? controlTypeOptionList.find(i => i.value === item.relational_id).label :
                item.type === 'media' ? mediaTypeOptionsList.find(i => i.value === item.relational_id).label : '-';
        } else if (column.key === 'order') {
            return <UpDown
                index={index}
                onUpClick={index === 0 ? undefined : onUpClick}
                onDownClick={(index === contentListSorted.length - 1) ? undefined : onDownClick}/>;
        }

        return item[column.key] || '-';
    }, [t, controlTypeOptionList, mediaTypeOptionsList, onUpClick, contentListSorted.length, onDownClick]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('content'),
            key: 'type',
        },
        {
            length: 2,
            name: t('type'),
            key: 'relational_id',
        },
        {
            length: 3,
            name: t('order'),
            key: 'order',
        },
    ], [t]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                backUrl='/settings/left-menu'>
                <SettingsList
                    columns={columns}
                    items={contentListSorted}
                    listType="leftMenuContentList"
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedContent}
                    onItemSelected={setSelectedContent}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
